import React, { useState } from "react";

import Dialog from "@material-ui/core/Dialog";
import Button from "@material-ui/core/Button";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import StableEntriesDecisions from "./StableEntriesDecisions";
import PreferenceProperty from "./PreferenceProperty";
import { IPreferenceProperty } from "./PreferenceProperty/PreferenceProperty";
import useStyles from "./styles";

export interface StableEntryInfo {
  horseName: string;
  status: racehorse360.StableEntryStatus;
}

export interface IReviewFormPopupData {
  trainerName: string;
  sleepingRoomsNumber: number;
  firstChoiceFacility: racehorse360.IFacility;
  secondChoiceFacility: racehorse360.IFacility;
  arrivalDate: string;
  stableEntries: StableEntryInfo[];
}

export interface IPreferences {
  firstChoiceSleepingRoomsNumber: number;
  firstChoiceStallsNumber: number;
  secondChoiceSleepingRoomsNumber: number;
  secondChoiceStallsNumber: number;
}

interface IProps {
  onClose: () => void;
  onSubmit: (preferences: IPreferences) => void;
  isFormSubmitting: boolean;
  data: IReviewFormPopupData;
}

const ReviewFormPopup = (props: IProps) => {
  const {
    onClose,
    onSubmit,
    isFormSubmitting,
    data: {
      stableEntries,
      firstChoiceFacility,
      secondChoiceFacility,
      sleepingRoomsNumber,
      arrivalDate,
      trainerName
    }
  } = props;

  const classes = useStyles();

  const [firstChoiceSleepingRoomsNumber, setFirstChoiceSleepingRoomsNumber] =
    useState(sleepingRoomsNumber);
  const [firstChoiceStallsNumber, setFirstChoiceStallsNumber] = useState(
    stableEntries.length
  );
  const [secondChoiceSleepingRoomsNumber, setSecondChoiceSleepingRoomsNumber] =
    useState(0);
  const [secondChoiceStallsNumber, setSecondChoiceStallsNumber] = useState(0);

  const isSubmitButtonDisabled = [
    firstChoiceSleepingRoomsNumber && !firstChoiceStallsNumber,
    secondChoiceSleepingRoomsNumber && !secondChoiceStallsNumber,
    isFormSubmitting
  ].some(Boolean);

  const areFacilitiesChoicesIdentical =
    firstChoiceFacility.id === secondChoiceFacility.id;
  const preferencesProperties: IPreferenceProperty[] = [
    {
      title: `${firstChoiceFacility.code} Sleeping Rooms`,
      value: firstChoiceSleepingRoomsNumber,
      onChange: setFirstChoiceSleepingRoomsNumber,
      maxLength: 2
    },
    {
      title: `${firstChoiceFacility.code} Stalls`,
      value: firstChoiceStallsNumber,
      onChange: setFirstChoiceStallsNumber,
      maxLength: 7
    },
    {
      title: `${secondChoiceFacility.code} Sleeping Rooms`,
      value: secondChoiceSleepingRoomsNumber,
      onChange: setSecondChoiceSleepingRoomsNumber,
      isFacilityRepeatable: areFacilitiesChoicesIdentical,
      maxLength: 2
    },
    {
      title: `${secondChoiceFacility.code} Stalls`,
      value: secondChoiceStallsNumber,
      onChange: setSecondChoiceStallsNumber,
      isFacilityRepeatable: areFacilitiesChoicesIdentical,
      maxLength: 7
    }
  ];

  const handleSubmit = () => {
    if (isSubmitButtonDisabled) return;

    onSubmit({
      firstChoiceSleepingRoomsNumber,
      firstChoiceStallsNumber,
      secondChoiceSleepingRoomsNumber,
      secondChoiceStallsNumber
    });
  };

  const renderPreferencesProperties = () => {
    return preferencesProperties
      .filter(property => !property.isFacilityRepeatable)
      .map((property, index) => (
        <PreferenceProperty key={index} property={property} />
      ));
  };

  return (
    <Dialog open={true} onClose={null} classes={{ paper: classes.dialogPaper }}>
      <DialogTitle classes={{ root: classes.dialogTitle }}>
        Submit Application Decision?
      </DialogTitle>

      <DialogContent classes={{ root: classes.dialogContent }}>
        <div className={classes.trainerName}>{trainerName}</div>
        <div className={classes.stableEntriesInfo}>
          {`Arrival - ${arrivalDate}`}
        </div>

        {renderPreferencesProperties()}

        <StableEntriesDecisions stableEntries={stableEntries} />
      </DialogContent>

      <DialogActions classes={{ root: classes.dialogActions }}>
        <Button className={classes.backButton} onClick={onClose}>
          Back
        </Button>
        <Button
          className={classes.submitButton}
          disabled={isSubmitButtonDisabled}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReviewFormPopup;
